<template>
  <zm-context :system-fonts="false" :namespace="NAME_SPACE">
    <Teleport to="#common-header-logo-container .frame-common-header__logo-image">
      <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
      <span v-if="isZoomMilSite" class="gov-logo-text">for Defense</span>
      <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
      <span v-else-if="isZoomGovSite" class="gov-logo-text">for Government</span>
    </Teleport>
    <div
      class="left-slot-container"
      :class="{ 'is-mobile-viewport': isMobile }"
      ref="left-slot-container">
      <custom-items v-if="!isMobile" :max-width="maxWidth" :actions="actions"></custom-items>
      <div v-if="showGlobalSearch" class="left-slot-search">
        <global-search :is-mobile-viewport="isMobile" :search-level="searchLevel" />
      </div>
    </div>
  </zm-context>
</template>

<script setup>
import { computed, ref, useTemplateRef, onMounted, onUnmounted, defineAsyncComponent } from 'vue'
import { ZmContext } from '@zoom/zoom-ui-vue3'
import { NAME_SPACE } from '@/commons/constants'
import customItems from './custom-items.vue'
import { isMobileViewport, createMobileWatcher } from '../common-header/responsive.js'

defineOptions({ name: 'frame-header-left' })

const globalSearch = defineAsyncComponent(() => import('../global-search/global-search.vue'))

const DISCOVER_PRODUCTS_ID = 'discoverProducts'
const PRICING_ID = 'pricing'

const props = defineProps({
  customs: {
    type: Array,
    default: () => []
  }
})

const isAdminSearch = window.microsDatas.isAdminMenu && window.microsDatas.enabledAdminCenterSearch
const isUserSearch = Boolean(window.microsDatas.isPersonalCenter)
const showGlobalSearch = isAdminSearch || isUserSearch
const searchLevel = isAdminSearch ? 'admin' : 'user'

const isZoomGovSite = computed(() => window.microsDatas.isZoomGovSite ?? false)
const hostName = location.hostname
const isZoomMilSite = computed(() => {
  if (window.microsDatas?.isZoomMilSite) {
    return true
  }
  return (
    hostName === 'zoomgov.mil' ||
    hostName.endsWith('.zoomgov.mil') ||
    hostName === 'zoommildev.com' ||
    hostName.endsWith('.zoommildev.com')
  )
})

const slotLeftContainer = useTemplateRef('left-slot-container')

const maxWidth = ref(null)
const isMobile = ref(isMobileViewport())

const SEARCH_BUTTON_WIDTH = 320

let resizeObserver = null
let mutationObserver = null
let mobileWatcher = null
let observedSearchButton = null

const getSearchReservedWidth = (el) => {
  const searchSlot = el.querySelector('.left-slot-search')
  if (!searchSlot) return 0
  const button = searchSlot.querySelector('.global-search-header-button')
  if (button) return button.getBoundingClientRect().width
  return SEARCH_BUTTON_WIDTH
}

const updateMaxWidth = () => {
  const el = slotLeftContainer.value
  if (!el) return
  const searchButton = el.querySelector('.global-search-header-button')
  if (searchButton && searchButton !== observedSearchButton && resizeObserver) {
    resizeObserver.observe(searchButton)
    observedSearchButton = searchButton
  }
  maxWidth.value = Math.max(0, el.clientWidth - getSearchReservedWidth(el))
}

onMounted(() => {
  resizeObserver = new ResizeObserver(updateMaxWidth)

  if (slotLeftContainer.value) {
    resizeObserver.observe(slotLeftContainer.value)
    mutationObserver = new MutationObserver(updateMaxWidth)
    mutationObserver.observe(slotLeftContainer.value, { childList: true, subtree: true })
  }

  updateMaxWidth()

  mobileWatcher = createMobileWatcher({
    onEnter: () => {
      isMobile.value = true
    },
    onLeave: () => {
      isMobile.value = false
    }
  })
  mobileWatcher.start()
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
    resizeObserver = null
  }
  if (mutationObserver) {
    mutationObserver.disconnect()
    mutationObserver = null
  }
  observedSearchButton = null
  if (mobileWatcher) {
    mobileWatcher.stop()
    mobileWatcher = null
  }
})
const { isLoggedInMA, isExternalUserView } = window.microsDatas

const actions = computed(() => {
  if (isLoggedInMA || isExternalUserView) {
    return []
  }
  const list = props.customs ?? []
  if (!Array.isArray(list) || (Array.isArray(list) && !list.length)) {
    return []
  }
  if (isZoomGovSite.value || isZoomMilSite.value) {
    const discoverProductsItem = list.find((item) => item.id === DISCOVER_PRODUCTS_ID)
    const pricingItem = list.find((item) => item.id === PRICING_ID)
    if (discoverProductsItem) {
      discoverProductsItem.visible = false
    }
    if (pricingItem) {
      pricingItem.visible = false
    }
  }
  return list.filter((item) => item?.visible !== false)
})
</script>

<style lang="scss">
.frame-common-header__logo-image {
  &:hover {
    text-decoration: none;
  }
}
.frame-common-header__logo-text {
  white-space: nowrap;
}
.left-slot-container {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  overflow: hidden;
  margin-right: 24px;
}
.left-slot-search {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  .global-search-container {
    flex: 0 1 320px;
    min-width: 0;
  }
}
.is-mobile-viewport .left-slot-search {
  padding-right: 12px;
  justify-content: end;
  min-width: initial;
  .global-search-container {
    flex: 0 0 auto;
  }
}
.gov-logo-text {
  line-height: 20px;
  color: var(--zoom-color-text-stronger-neutral);
  font-size: 24px;
  margin-left: 8px;
}
.products-list-popover {
  background-color: transparent;
  border: none;
  box-shadow: none;
  margin-left: 8px;
  left: 10px !important;
  .fe-popups-popover {
    max-width: initial;
    padding: 0;
  }
}

.products-list-button {
  color: inherit;
  .fe-popups-icon {
    transition: transform 0.3s ease-in-out;
  }
}

.header-custom-link {
  line-height: 32px;
  padding-left: 12px;
  padding-right: 12px;
  border-radius: 12px;
  color: inherit;
}
.frame-common-header__left .header-custom-link + .header-custom-link {
  margin-left: 0;
}
.vanity-site {
  .frame-common-header__logo-text,
  .header-custom-link,
  .products-list-button {
    color: inherit;
  }
}
@media screen and (max-width: 768px) {
  .discover-products-container {
    display: none;
  }
  .header-custom-link {
    display: none;
  }
}
</style>
